/**
 * Applications Copilot — composer (panel footer).
 *
 * Glass bar with a gradient border on focus. Auto-growing textarea (1–6
 * lines; Enter = send, Shift+Enter = newline), mic button (hold = push-to-talk,
 * click = hands-free toggle; listening shows wave bars + an interim-transcript
 * line above the bar), speaker toggle (off / on / speaking equalizer), Send
 * orb that turns into Stop while streaming, counter from 1,800 chars and a
 * rate-limit countdown that replaces the helper line. `ArrowUp` in an empty
 * box recalls the last user message.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Send, Square, Volume2, VolumeX, Loader2, RotateCcw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { cn } from '@/lib/utils';
import copilotTts from './copilotTts';
import { useCopilotVoice } from './useCopilotVoice';

export const COUNTER_THRESHOLD = 1800;
const HOLD_MS = 350;
const LINE_PX = 20; // text-[13px] leading-5
const MAX_LINES = 6;
const PAD_Y = 12; // py-1.5 top + bottom

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

function IconTip({ label, children }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="text-[11px]">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Wave({ bars = 5, testId }) {
  return (
    <span className="cp-wave" data-testid={testId} aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => (
        <i key={i} />
      ))}
    </span>
  );
}

const CopilotComposer = React.forwardRef(function CopilotComposer(
  {
    value,
    onChange,
    onSend,
    onStop,
    isStreaming = false,
    disabled = false,
    maxChars = 2000,
    lastUserMessage = '',
    voiceEnabled = true,
    ttsEnabled = false,
    ttsSpeaking = false,
    onToggleTts,
    retryIn = 0,
    placeholder = 'Ask about these candidates…',
  },
  forwardedRef
) {
  const localRef = useRef(null);
  const textareaRef = forwardedRef || localRef;
  const [interimText, setInterimText] = useState('');
  const holdTimerRef = useRef(null);
  const holdingRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  const voice = useCopilotVoice({
    onStart: () => copilotTts.bargeIn(),
    onInterim: (text) => setInterimText(text),
    onFinal: (segment) => {
      setInterimText('');
      const base = valueRef.current ? `${valueRef.current.replace(/\s+$/, '')} ` : '';
      onChange(`${base}${segment}`, { fromVoice: true });
    },
    onAutoSend: () => {
      if (String(valueRef.current || '').trim()) onSend({ voice: true });
    },
  });

  // Auto-grow textarea, 1–6 lines.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, LINE_PX * MAX_LINES + PAD_Y)}px`;
  }, [value, textareaRef]);

  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      if (isStreaming || retryIn > 0) return;
      if (String(value || '').trim()) onSend({ voice: Boolean(voice.engine) && voice.listening });
      return;
    }
    if (e.key === 'ArrowUp' && !value && lastUserMessage) {
      e.preventDefault();
      onChange(lastUserMessage);
    }
  };

  const handleChange = (e) => {
    copilotTts.bargeIn();
    onChange(e.target.value.slice(0, maxChars));
  };

  const micDenied = voice.error?.code === 'mic_denied';
  const micHidden = !voiceEnabled || !voice.supported || voice.error?.code === 'unsupported';
  const handsFree = voice.listening && voice.mode === 'handsfree';

  // Mic: pointer-down starts a hold timer. Released before HOLD_MS → toggle
  // hands-free. Held → push-to-talk until release. Denied → click retries.
  const onMicDown = useCallback(
    (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      if (micDenied) return;
      holdingRef.current = false;
      holdTimerRef.current = window.setTimeout(() => {
        holdingRef.current = true;
        voice.setMode('ptt');
        void voice.start();
      }, HOLD_MS);
    },
    [voice, micDenied]
  );

  const onMicUp = useCallback(() => {
    clearHoldTimer();
    if (micDenied) {
      voice.retry();
      return;
    }
    if (holdingRef.current) {
      holdingRef.current = false;
      voice.stop();
      return;
    }
    // short click → hands-free toggle
    if (voice.listening) voice.stop();
    else {
      voice.setMode('handsfree');
      void voice.start();
    }
  }, [voice, micDenied]);

  const onMicLeave = () => {
    clearHoldTimer();
    if (holdingRef.current) {
      holdingRef.current = false;
      voice.stop();
    }
  };

  useEffect(() => () => holdTimerRef.current && window.clearTimeout(holdTimerRef.current), []);

  const length = String(value || '').length;
  const counterFrom = Math.max(0, Math.min(COUNTER_THRESHOLD, maxChars - 200));
  const showCounter = length >= counterFrom;
  const nearLimit = length >= maxChars - 50;
  const overLimit = length > maxChars;
  const rateLimited = retryIn > 0;
  const canSend = !disabled && !isStreaming && !rateLimited && String(value || '').trim().length > 0 && !overLimit;

  const micLabel = micDenied
    ? 'Microphone blocked — click to retry'
    : voice.listening
      ? handsFree
        ? 'Listening (hands-free) — click to stop'
        : 'Listening — release to stop'
      : 'Hold to talk · click for hands-free';

  const ttsLabel = ttsEnabled ? (ttsSpeaking ? 'Speaking — click to turn read aloud off' : 'Read answers aloud: on') : 'Read answers aloud: off';

  const otherVoiceError = voice.error && !micDenied && voice.error.code !== 'unsupported' ? voice.error : null;

  return (
    <div className="px-2 pb-2 pt-1.5" data-testid="copilot-composer">
      {voice.listening && (
        <div className="mb-1.5 flex min-h-[20px] items-center gap-2 px-1 text-[12px] leading-4" data-testid="copilot-listening">
          <span className="cp-text-brand font-medium">Listening…</span>
          {interimText && <span className="cp-muted min-w-0 truncate italic">{interimText}</span>}
          {handsFree && (
            <span className="cp-chip-mint ml-auto inline-flex h-5 shrink-0 items-center rounded-full border px-2 text-[10px] font-semibold">
              Hands-free
            </span>
          )}
        </div>
      )}

      {otherVoiceError && (
        <div className="cp-banner cp-banner-warn mb-1.5" role="status">
          <MicOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{otherVoiceError.message}</span>
          <button type="button" className="cp-focus inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium hover:bg-black/5" onClick={voice.retry}>
            <RotateCcw className="h-3 w-3" aria-hidden="true" /> Retry
          </button>
        </div>
      )}

      <div
        className={cn('cp-composer cp-gradient-border flex items-end gap-1 px-1.5 py-1', handsFree && 'ring-2 ring-[hsl(var(--cp-mint))] ring-offset-1')}
        data-active={voice.listening ? 'true' : undefined}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={voice.listening ? 'Listening…' : placeholder}
          rows={1}
          disabled={disabled}
          aria-label="Message the copilot"
          className="min-h-[32px] flex-1 resize-none self-center bg-transparent px-1.5 py-1.5 text-[13px] leading-5 outline-none placeholder:text-[var(--cp-text-faint)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          style={{ color: 'var(--cp-text)' }}
        />

        {!micHidden && (
          <IconTip label={micLabel}>
            <button
              type="button"
              className={cn('cp-icon-btn cp-icon-btn-lg shrink-0', voice.listening && 'cp-mint-fill', micDenied && 'cp-icon-btn-bad')}
              onPointerDown={onMicDown}
              onPointerUp={onMicUp}
              onPointerLeave={onMicLeave}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (micDenied) voice.retry();
                  else {
                    voice.setMode('handsfree');
                    voice.toggle();
                  }
                }
              }}
              aria-pressed={voice.listening}
              aria-label={micLabel}
              data-testid="copilot-mic"
              disabled={disabled}
            >
              {voice.listening ? <Wave bars={5} testId="copilot-mic-wave" /> : micDenied ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          </IconTip>
        )}

        {voiceEnabled && onToggleTts && (
          <IconTip label={ttsLabel}>
            <button
              type="button"
              className="cp-icon-btn cp-icon-btn-lg shrink-0"
              onClick={onToggleTts}
              aria-pressed={ttsEnabled}
              aria-label="Toggle read aloud"
              data-on={ttsEnabled ? 'true' : undefined}
              data-testid="copilot-speaker"
            >
              {ttsEnabled && ttsSpeaking ? <Wave bars={3} testId="copilot-tts-wave" /> : ttsEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
          </IconTip>
        )}

        {isStreaming ? (
          <IconTip label="Stop">
            <button type="button" className="cp-orb cp-orb-stop h-10 w-10 shrink-0" onClick={onStop} aria-label="Stop generating">
              <Square className="h-3.5 w-3.5 fill-current" />
            </button>
          </IconTip>
        ) : (
          <IconTip label={rateLimited ? `Try again in ${retryIn} s` : 'Send (Enter)'}>
            <button
              type="button"
              className="cp-orb h-10 w-10 shrink-0"
              onClick={() => onSend({ voice: false })}
              disabled={!canSend}
              aria-label="Send"
            >
              {disabled ? <Loader2 className="h-4 w-4 motion-safe:animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </IconTip>
        )}
      </div>

      <div className="cp-muted mt-1 flex items-center justify-between px-1 text-[11px] leading-4">
        {rateLimited ? (
          <span role="status" className="cp-text-warn cp-num font-medium" data-testid="copilot-retry-in">
            Try again in {retryIn} s
          </span>
        ) : (
          <span>Enter to send · Shift+Enter new line</span>
        )}
        {showCounter && (
          <span className={cn('cp-num', nearLimit && 'cp-text-warn font-medium')} aria-live="polite" data-testid="copilot-counter">
            {fmt(length)} / {fmt(maxChars)}
          </span>
        )}
      </div>
    </div>
  );
});

export default CopilotComposer;
